/**
 * The "momentum" contract of Outreach (owner request 2026-10-21: "I need to have a feeling things
 * are happening and my music is going out in the world"). Design: artifact "Outreach Momentum",
 * frames t1 (Plug is searching), t2 (campaign live), t3 (Pitch home live), t6 (states), g1-g3
 * (phone), u1-u5 (emails). Copy: docs/superpowers/design-reference/music24-outreach-momentum/.
 *
 * Every number and every name is real data. No client shows a count that the API did not send.
 * The clients pace the REVEAL of real rows (the search list appears one row at a time); they never
 * make up a row or a number. Pure types and pure helpers only: no I/O.
 */

/** The phase of a campaign as the artist sees it. */
export type OutreachLivePhase =
  /** The sound profile builds, or the target find runs, for this campaign's artist. */
  | 'searching'
  /** Plug writes the pitches (campaign status `writing`). */
  | 'writing'
  /** The pitches wait for the artist (campaign status `review`). */
  | 'review'
  /** Scheduled or sending. */
  | 'sending'
  | 'paused'
  | 'done'
  | 'draft';

/** One step of the search list (design t1, g1). */
export type OutreachSearchStepKey = 'sound' | 'similar' | 'scan' | 'contacts' | 'write' | 'send';
export type OutreachSearchStepState = 'done' | 'now' | 'wait';

export interface OutreachSearchStep {
  key: OutreachSearchStepKey;
  state: OutreachSearchStepState;
  /**
   * The real counter of the step, or null when the step has no number yet.
   * sound: genres; similar: similar artists; scan: playlists scanned; contacts: matches with an
   * email; write: pitches written; send: pitches sent.
   */
  count: number | null;
  /** Only for `scan` and `write`: the total, when it is known. */
  total: number | null;
}

/** One matched curator (a target match), in the order Plug found it. */
export interface OutreachLiveMatch {
  targetUuid: string;
  name: string;
  /** `OutreachTargetKind`: playlist, press, radio, creator, ... */
  kind: string;
  /** The streaming service of a playlist (`spotify`, `appleMusic`, ...), else null. */
  service: string | null;
  followers: number | null;
  coverUrl: string | null;
  /** 0-100 (fym-shared `fitScore.ts`). */
  fit: number;
  /** One short reason: the first reason of the match (a similar artist, a genre). */
  why: string | null;
  hasEmail: boolean;
  /** ISO time of the match row. The clients animate the rows newer than their last view. */
  foundAt: string;
}

export interface OutreachLiveSearch {
  steps: OutreachSearchStep[];
  /** The genres of the sound profile (at most 5). */
  genres: string[];
  /** Names of similar artists (at most 3), for the "Like X" chips. */
  similarArtists: string[];
  /** The best matches, best fit first (at most 12). */
  matches: OutreachLiveMatch[];
  /** All matches with an email for this campaign's artist. */
  matchCount: number;
  /** Playlists still to scan in the running find, or null when no find runs. */
  playlistsLeft: number | null;
}

/** A counter with the part that happened today (UTC day of the artist's time zone). */
export interface OutreachCount {
  total: number;
  today: number;
}

export interface OutreachLiveTotals {
  pitched: OutreachCount;
  opened: OutreachCount;
  /** A listen of 30 s or more (`isPitchListen`). */
  listened: OutreachCount;
  /** Human replies only (no out-of-office, bounce or unsubscribe). */
  replied: OutreachCount;
  placed: OutreachCount;
  /** The name of the first placement, for the stat card ("Quiet Forest Mornings"). */
  firstPlacementName: string | null;
}

/**
 * One pitch that waits for its send time. It holds no name and no picture: the recipient was not
 * pitched yet ("names show only after the pitch").
 */
export interface OutreachUpNext {
  /** The target kind (`playlist`, `blog`, …) or null. */
  kind: string | null;
  /** ISO send time of the message. */
  sendAt: string;
  /** The IANA time zone of the recipient, or null. The clients show the time in it ("their time"). */
  timeZone: string | null;
  /** City or country name for the line "to someone in Berlin", or null. */
  place: string | null;
}

export interface OutreachLiveToday {
  sent: number;
  /** The cap of the campaign's mailboxes today (sum of `mailboxDailyCap`). */
  cap: number;
  next: OutreachUpNext | null;
  /** The next 2 messages after `next`. */
  upNext: OutreachUpNext[];
  /** The scheduled messages after `upNext`. */
  queuedMore: number;
}

/**
 * Where a planned pitch is (owner request 2026-10-27: "show the next emails we plan to send, so I
 * see something is going on"): `writing` = Plug writes it (no text yet); `review` = written, it
 * waits for the artist (review mode); `ready` = approved, it waits for a send time; `scheduled` =
 * it has a send time; `sending` = it goes out now.
 */
export type OutreachPlannedState = 'writing' | 'review' | 'ready' | 'scheduled' | 'sending';

/**
 * One step-1 pitch of the campaign that did not go out yet. It names the person: the owner decided
 * on 2026-10-27 that a planned pitch shows the name and the cover before the send. Never the email.
 */
export interface OutreachPlannedPitch {
  messageUuid: string;
  /** The playlist or outlet name (else the person's name). */
  name: string;
  coverUrl: string | null;
  /** The target kind (`playlist`, `press`, …) or null. */
  kind: string | null;
  /** The streaming service of a playlist (`spotify`, `appleMusic`, ...), else null. */
  service: string | null;
  followers: number | null;
  /** The country name ("Germany"), or null. */
  place: string | null;
  /** The IANA time zone of the recipient, or null. */
  timeZone: string | null;
  /** ISO send time; null while the pitch has none (`writing`, `review`, `ready`). */
  sendAt: string | null;
  /**
   * ISO earliest send time of a pitch with no `sendAt` yet: the first slot of the campaign's send
   * window in the recipient's zone, not before the start of a next-day batch's day (a Friday start
   * after 11:00 with weekdays only gives Monday). Null when `sendAt` is set.
   */
  notBefore: string | null;
  state: OutreachPlannedState;
  /** The subject line once Plug wrote the pitch; null while it writes. */
  subject: string | null;
}

/** The most planned pitches of the live answer. A day holds at most 15 (Label). */
export const OUTREACH_LIVE_MAX_PLANNED = 20;

export interface OutreachLivePlanned {
  /** Send time first (the pitches with no time last), then the order of the pick. */
  pitches: OutreachPlannedPitch[];
  /** Every planned pitch of the campaign (it can be more than `pitches`). */
  total: number;
  /**
   * ISO time of the next daily batch that picks new people for the artist (06:00 artist time, on a
   * day that has no batch yet). Null when the campaign does not pick (paused).
   */
  nextBatchAt: string | null;
}

/** The warm-up facts of the campaign's first sending mailbox (fym-shared `mailboxWarmup`). */
export interface OutreachLiveWarmup {
  mailboxAddress: string;
  warming: boolean;
  day: number;
  totalDays: number;
  todayCap: number;
  nextCap: number | null;
  /** ISO. */
  nextCapAt: string | null;
  health: string;
}

/** `outreach.campaigns.live({ accountId, campaignUuid })`. */
export interface OutreachCampaignLive {
  campaignUuid: string;
  phase: OutreachLivePhase;
  song: { catalogItemUuid: string; title: string; coverUrl: string | null };
  /** Whole days since the first send, 1-based; null before the first send. */
  liveDay: number | null;
  /** Present in the phases `searching`, `writing` and `review` (design t1, g1). */
  search: OutreachLiveSearch | null;
  totals: OutreachLiveTotals;
  /** Present in the phases `sending` and `paused`. */
  today: OutreachLiveToday | null;
  /** The pitches that did not go out yet, with names. Present in the phases `sending` and `paused`. */
  planned: OutreachLivePlanned | null;
  warmup: OutreachLiveWarmup | null;
  /** The mailbox that sends ("from laziibee@gmail.com"), or null with none. */
  mailboxAddress: string | null;
  /** The mailbox needs a reconnect: sending stopped (design t6 "Sending stopped"). */
  mailboxNeedsReconnect: boolean;
  /** Scheduled messages that wait (for the reconnect state). */
  waitingCount: number;
}

/** One line of the activity timeline (design t2, t3, g2, u4). */
export type OutreachActivityKind =
  | 'sent'
  | 'followUp'
  | 'opened'
  | 'listened'
  | 'clicked'
  | 'replied'
  | 'placed'
  | 'found'
  | 'ready';

export interface OutreachActivityEvent {
  /** Stable and unique: `<kind>:<source row id>`. */
  id: string;
  kind: OutreachActivityKind;
  /** ISO. */
  at: string;
  campaignUuid: string | null;
  songTitle: string | null;
  /** The curator or the target. Null for `found` and `ready` (Plug is the actor). */
  who: {
    name: string;
    kind: string | null;
    followers: number | null;
    coverUrl: string | null;
  } | null;
  /** `listened`: seconds. */
  listenedSeconds: number | null;
  /** `clicked`: the service id. */
  service: string | null;
  /** `replied`: the reply label (`interested`, `placed`, `later`, `notAFit`, `payment`, `other`). */
  replyLabel: string | null;
  /** `replied`: at most 140 characters of the reply text. */
  snippet: string | null;
  /** `found`, `ready`: the count; `placed`: the playlist position or null. */
  count: number | null;
  replyUuid: string | null;
  messageUuid: string | null;
}

/** `outreach.campaigns.activity({ accountId, campaignUuid?, cursor?, limit? })`. */
export interface OutreachActivityPage {
  events: OutreachActivityEvent[];
  nextCursor: string | null;
}

export const OUTREACH_ACTIVITY_MAX_LIMIT = 50;

/** The live block of the Pitch home (design t3). Null when no campaign of the artist has sent. */
export interface OutreachHomeLive {
  campaignUuid: string;
  songTitle: string;
  coverUrl: string | null;
  pitched: number;
  /** The last 7 days. */
  listened7d: number;
  replied7d: number;
  placed7d: number;
  placedTotal: number;
  /** The pitches planned for the next send day of the campaign's mailboxes. */
  nextDayCount: number;
  /** ISO 3166 codes of the recipients that got a pitch, most first (at most 8). */
  countries: Array<{ code: string; count: number }>;
  countryCountTotal: number;
}

/** The time between two rows of the search reveal on a first view, in ms (design t1). */
export const OUTREACH_SEARCH_REVEAL_STEP_MS = 450;

/**
 * The rows that a client animates: the rows found after the client's last view of the screen
 * (`lastSeenAt`), or every row on a first view (null). Pure: the clients and the tests share it.
 */
export function matchesToReveal(matches: OutreachLiveMatch[], lastSeenAt: string | null): OutreachLiveMatch[] {
  if (lastSeenAt === null) {
    return matches;
  }
  const seen = Date.parse(lastSeenAt);
  return matches.filter((match) => Date.parse(match.foundAt) > seen);
}
